import { useQuery } from "@tanstack/react-query";
import { useToast } from "ui-kit";
import client from "libs/client";
import type { SelectValue } from "ui-kit/src/MultipleSelect/types";
import { DieLigenTasksDataSchema } from "../../types/apiManagement";

interface HeaderFilterState {
    league: SelectValue | null;
    video_status: SelectValue | null;
    teamsheet_status: SelectValue | null;
    home_team: SelectValue | null;
    away_team: SelectValue | null;
    country: SelectValue | null;
    category: SelectValue | null;
    eyeball_game_id: SelectValue | null;
}

interface PaginationState {
    pageIndex: number;
    pageSize: number;
}

interface SortingState {
    fields: {
        [key: string]: 'asc' | 'desc' | null;
    };
}

const getSortString = (sorting: SortingState): string => {
    const fieldMap: Record<string, string> = {
        gameDate: 'game_date',
        updatedAt: 'updated_at'
    };

    const sortParts: string[] = [];

    Object.entries(sorting.fields).forEach(([field, direction]) => {
        const mappedField = fieldMap[field];
        if (mappedField && direction) {
            const directionSymbol = direction === 'asc' ? '+' : '-';
            sortParts.push(`${mappedField}${directionSymbol}`);
        }
    });

    if (sortParts.length === 0) {
        return "";
    }

    return sortParts.join(',');
};

export const useVideoSourceData = (
    pagination: PaginationState, 
    headerFilters: HeaderFilterState,
    sorting?: SortingState
) => {
    const { addErrorToast } = useToast();

    const { data: tasksData, isLoading, isFetching } = useQuery({
        queryKey: ['dieLigenTasks', pagination.pageIndex, pagination.pageSize, headerFilters, sorting],
        queryFn: async () => {
            const filterParams: Record<string, string | number | null> = {
                ...(headerFilters.league?.value ? { league: headerFilters.league.value } : {}),
                ...(headerFilters.away_team?.value ? { away: headerFilters.away_team.value } : {}),
                ...(headerFilters.home_team?.value ? { home: headerFilters.home_team.value } : {}),
                ...(headerFilters.video_status?.value ? { video_status: headerFilters.video_status.value } : {}),
                ...(headerFilters.teamsheet_status?.value ? { teamsheet_status: headerFilters.teamsheet_status.value } : {}),
                ...(headerFilters.country?.value ? { country: headerFilters.country.value } : {}),
                ...(headerFilters.eyeball_game_id?.value ? { eyeball_game_id: headerFilters.eyeball_game_id.value } : {})
            };
            
            const params = {
                filters: {
                    page: pagination.pageIndex + 1,
                    limit: pagination.pageSize,
                    sort: getSortString(sorting || { fields: {} }),
                    ...filterParams
                }
            };
            
            try {
                const response = await client.getTasks.query(params);
                const parsedData = DieLigenTasksDataSchema.parse(response.data);
                return parsedData;
            } catch (error) {
                addErrorToast(`Failed to parse Die Ligen tasks data: ${String(error)}`);
                return { videos: [], total: 0 };
            }
        },
        keepPreviousData: true,
        staleTime: 30000,
        cacheTime: 5 * 60 * 1000,
        refetchOnWindowFocus: false
    });

    return {
        tasksData,
        isLoading,
        isFetching
    };
}; 